import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
  Alert,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import dayjs from "dayjs";

// Placeholder mapping for assignment data
const PLACEHOLDER_MAP = {
  ClientName: "Client?.Account",
  ClientAddress: "Client?.Address",
  LinguistName: "Linguist?.ShortName||Linguist?.Name||LinguistName",
  LinguistNumber: "Linguist?.PhoneNumber",
  LinguistAddress: "Linguist?.Address",
  AssignmentName: "Name",
  AssignmentDuration: "Duration",
  AssignmentStartTime: "TimeStart",
  AssignmentEndTime: "TimeEnd",
  AssignmentDate: "Date",
  AssignmentDetails: "Details||Name",
  AssignmentRequestor: "Requestor?.Name",
  AssignmentContactPerson: "ContactPersonName",
  AssignmentContactPersonNumber: "ContactPersonNumber",
  AssignmentType: "ServiceSubtype?.Name||InterpretationType",
  AssignmentLanguage: "ServiceTargetLanguages.0.Language.Name",
  AssignmentAddressLink: "Address",
  AssignmentPrepTime: "PreparationTime",
  Default: "NO_DATA_REPLACE_MANUALLY",
};

const formatTimeToHHMM12Hr = (timeStr) => {
  if (!timeStr) return "";
  const timeParts = timeStr.split(":");
  if (!timeParts[0] || !timeParts[1]) return timeStr;

  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
};

const formatDurationToHHMM = (durationStr) => {
  if (!durationStr) return "";
  const parts = durationStr.split(":");
  if (!parts[0] || !parts[1]) return durationStr;

  return `${parts[0]}:${parts[1]}`;
};

const stripHtmlTags = (html) => {
  if (!html) return "";

  // Convert closing block-level tags to newlines (handles <p>, <div>, <li>, etc.)
  let text = html.replace(/<\/(p|div|li|tr|blockquote|h[1-6])>/gi, "\n");

  // Convert <br> to newlines
  text = text.replace(/<br\s*\/?>/gi, "\n");

  // Remove all remaining HTML tags
  text = text.replace(/<[^>]*>/g, "");

  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  // Collapse 3+ consecutive newlines to max 2 (one blank line)
  text = text.replace(/\n{3,}/g, "\n\n");

  return text.trim();
};

const replacePlaceholders = (text, assignmentData) => {
  if (!text) return "";

  let result = text;
  const placeholderRegex = /\{([^}]+)\}/g;

  result = result.replace(placeholderRegex, (match, placeholder) => {
    const path = PLACEHOLDER_MAP[placeholder];
    if (!path) return match;

    const pathOptions = path.split("||").map((p) => p.trim());
    let value = null;

    for (const option of pathOptions) {
      const keys = option.split("?").join(".").split(".");
      let currentValue = assignmentData;

      for (const key of keys) {
        if (key && currentValue) {
          currentValue = currentValue[key];
        }
      }

      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ""
      ) {
        value = currentValue;
        break;
      }
    }

    if (value === undefined || value === null || value === "") {
      return '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>';
    }

    if (
      placeholder === "AssignmentStartTime" ||
      placeholder === "AssignmentEndTime"
    ) {
      return formatTimeToHHMM12Hr(value);
    }

    if (placeholder === "AssignmentDuration") {
      return formatDurationToHHMM(value);
    }

    if (placeholder === "AssignmentDate") {
      return dayjs.utc(value).format("dddd, MMMM DD, YYYY");
    }

    return (
      value || '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>'
    );
  });

  return result;
};

const getDefaultDate = () => {
  const today = dayjs();
  const dayOfWeek = today.day();

  // If Friday (5), return Monday (add 3 days)
  if (dayOfWeek === 5) {
    return today.add(3, "day");
  }

  // Otherwise return tomorrow
  return today.add(1, "day");
};

const getAssignmentStatus = (assignment) => {
  return (assignment?.InterpretationStatus || "")
    .toString()
    .trim();
};

const normalizeStatus = (status) =>
  status
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const isCancelledAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  return status.startsWith("cancelled") || status.includes("cancelled");
};

const isCompletedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  return status.includes("completed");
};

const isConfirmedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  if (!status) return false;
  if (isCancelledAssignment(assignment) || isCompletedAssignment(assignment)) {
    return false;
  }

  return status.includes("confirmed");
};

const isRequestedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  if (!status) return false;
  if (isCancelledAssignment(assignment) || isCompletedAssignment(assignment)) {
    return false;
  }

  return status.includes("requested");
};

const getVisibleAssignmentsForLinguist = (linguist, excludeRequested) => {
  const confirmedAssignments = linguist?.confirmedAssignments || [];
  const requestedAssignments = linguist?.requestedAssignments || [];

  return excludeRequested
    ? confirmedAssignments
    : [...confirmedAssignments, ...requestedAssignments];
};

const InterpretationReminders = () => {
  usePageTitle("Interpretation Reminders");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { setOverlayName, showSnackbar, openDialog, closeDialog } =
    useStateContext();

  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [assignmentModifier, setAssignmentModifier] = useState(null);
  const [smsModifiers, setSmsModifiers] = useState([]);
  const [linguists, setLinguists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [editingLinguistId, setEditingLinguistId] = useState(null);
  const [savedRange, setSavedRange] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [excludeRequestedGlobal, setExcludeRequestedGlobal] = useState(true);
  const editorRef = useRef(null);

  const isSettingsMenuOpen = Boolean(menuAnchorEl);

  const getVisibleAssignments = useCallback(
    (linguist) =>
      getVisibleAssignmentsForLinguist(linguist, excludeRequestedGlobal),
    [excludeRequestedGlobal],
  );

  const displayedLinguists = useMemo(
    () =>
      linguists.filter(
        (linguist) => getVisibleAssignments(linguist).length > 0,
      ),
    [linguists, getVisibleAssignments],
  );

  const closeOverlay = () => setOverlayName("");

  const handleSettingsMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleExcludeRequestedToggle = (checked) => {
    setExcludeRequestedGlobal(checked);

    if (!editingLinguistId) return;

    const editingLinguist = linguists.find((ling) => ling.id === editingLinguistId);
    if (!editingLinguist) {
      setEditingLinguistId(null);
      return;
    }

    const visibleAssignments = getVisibleAssignmentsForLinguist(
      editingLinguist,
      checked,
    );

    if (visibleAssignments.length === 0) {
      setEditingLinguistId(null);
    }
  };

  const handleModifierMouseDown = (e) => {
    e.preventDefault();
    // Save the current selection before the click
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0));
    }
  };

  const insertModifier = (modifier) => {
    const el = editorRef.current;
    if (!el) {
      console.error("Editor ref is not available");
      return;
    }

    // Focus the editor
    el.focus();

    // Restore the saved selection if available
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
      setSavedRange(null);
    }

    // Insert at cursor position using execCommand
    document.execCommand("insertHTML", false, modifier.Body);

    // Re-run placeholder replacement on the updated content
    const updatedContent = el.innerHTML;
    const currentLinguist = linguists.find((l) => l.id === editingLinguistId);
    const visibleAssignments = currentLinguist
      ? getVisibleAssignments(currentLinguist)
      : [];

    if (currentLinguist && visibleAssignments.length > 0) {
      const replacedContent = replacePlaceholders(
        updatedContent,
        visibleAssignments[0],
      );
      el.innerHTML = replacedContent;

      // Move cursor to end after innerHTML change (since range positions are now invalid)
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Update the edited message in state immediately with replaced content
      setLinguists((prev) =>
        prev.map((ling) =>
          ling.id === editingLinguistId
            ? {
                ...ling,
                editedMessage: el.innerHTML,
                isCopied: false,
                isSent: false,
              }
            : ling,
        ),
      );
    }
  };

  // Fetch SMS templates and modifiers on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const [templatesResponse, modifierResponse, allModifiersResponse] =
          await Promise.all([
            api.get("/communicationTemplates?channel=SMS"),
            api.get(
              "/communicationTemplates?channel=SMS&type=Modifier&name=AssignmentReminderDetails",
            ),
            api.get("/communicationTemplates?type=Modifier"),
          ]);
        setSmsTemplates(templatesResponse.data || []);
        setSmsModifiers(allModifiersResponse.data || []);

        // Get the first modifier that matches (or find by name)
        const modifiers = modifierResponse.data || [];
        const modifier =
          modifiers.find((m) => m.Name === "AssignmentReminderDetails") ||
          modifiers[0];
        setAssignmentModifier(modifier);
      } catch (err) {
        console.error("Error fetching SMS templates:", err);
        showSnackbar("Error loading SMS templates", theme, "error");
      }
    };

    fetchTemplates();
  }, [showSnackbar, theme]);

  // Fetch linguists with assignments when date or smsTemplates change
  useEffect(() => {
    if (!selectedDate || smsTemplates.length === 0) return;

    const fetchLinguistsWithAssignments = async () => {
      setLoading(true);
      try {
        const dateStr = selectedDate.format("YYYY-MM-DD");
        const response = await api.get(
          `/services/by-date?date=${dateStr}&serviceCategory=Interpretation`,
        );

        // Group assignments by linguist and auto-select template
        const linguistMap = new Map();

        (response.data || []).forEach((assignment) => {
          const linguistId = assignment.Linguist?.id;
          if (!linguistId) return;

          if (!linguistMap.has(linguistId)) {
            linguistMap.set(linguistId, {
              id: linguistId,
              name: assignment.Linguist.Name,
              phoneNumber: assignment.Linguist.PhoneNumber,
              confirmedAssignments: [],
              requestedAssignments: [],
              hasRequestedAssignments: false,
              selectedTemplateId: null, // will set after grouping
              editedMessage: null,
              isCopied: false,
              isSent: false,
            });
          }

          const linguistEntry = linguistMap.get(linguistId);

          if (isConfirmedAssignment(assignment)) {
            linguistEntry.confirmedAssignments.push(assignment);
          } else if (isRequestedAssignment(assignment)) {
            linguistEntry.requestedAssignments.push(assignment);
            linguistEntry.hasRequestedAssignments = true;
          }
        });

        // For each linguist, pick the correct template based on first assignment's InterpretationType
        const updatedLinguists = Array.from(linguistMap.values())
          .filter(
            (ling) =>
              ling.confirmedAssignments.length > 0 ||
              ling.requestedAssignments.length > 0,
          )
          .map((ling) => {
            const visibleAssignments = getVisibleAssignmentsForLinguist(
              ling,
              excludeRequestedGlobal,
            );
            const fallbackAssignments = [
              ...ling.confirmedAssignments,
              ...ling.requestedAssignments,
            ];
            const firstAssignment =
              visibleAssignments[0] || fallbackAssignments[0] || null;
            let selectedTemplate = null;
            if (firstAssignment && firstAssignment.ServiceSubtype?.Name) {
              const type = firstAssignment.ServiceSubtype.Name.toLowerCase();
              if (type.includes("in-person")) {
                selectedTemplate = smsTemplates.find(
                  (t) =>
                    t.Channel === "SMS" &&
                    t.Name.toLowerCase().includes("in-person"),
                );
              } else if (
                type.includes("virtual") ||
                type.includes("phone") ||
                type.includes("opi") ||
                type.includes("on-demand")
              ) {
                selectedTemplate = smsTemplates.find(
                  (t) =>
                    t.Channel === "SMS" &&
                    t.Name.toLowerCase().includes("virtual"),
                );
              }
            }
            // Default to in-person if nothing else matches
            if (!selectedTemplate) {
              selectedTemplate = smsTemplates.find(
                (t) =>
                  t.Channel === "SMS" &&
                  t.Name.toLowerCase().includes("in-person"),
              );
            }
            return {
              ...ling,
              selectedTemplateId: selectedTemplate
                ? selectedTemplate.id
                : smsTemplates[0]?.id || null,
            };
          });

        setLinguists(updatedLinguists);
      } catch (err) {
        console.error("Error fetching assignments:", err);
        showSnackbar("Error loading assignments", theme, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchLinguistsWithAssignments();
  }, [selectedDate, smsTemplates, showSnackbar, theme, excludeRequestedGlobal]);

  const generateMessage = useCallback(
    (linguist, htmlMode = true) => {
      if (linguist.editedMessage) return linguist.editedMessage;

      const template = smsTemplates.find(
        (t) => t.id === linguist.selectedTemplateId,
      );
      const visibleAssignments = getVisibleAssignments(linguist);
      if (!template || visibleAssignments.length === 0) return "";

      let bodyText = htmlMode ? template.Body : stripHtmlTags(template.Body);

      // Handle {NumberOfAssignments}
      bodyText = bodyText.replace(
        /{NumberOfAssignments}/g,
        visibleAssignments.length.toString(),
      );

      // Handle {AssignmentsList}
      if (bodyText.includes("{AssignmentsList}") && assignmentModifier) {
        const assignmentLines = visibleAssignments.map((assignment) => {
          return replacePlaceholders(assignmentModifier.Body, assignment);
        });
        // Join without separator to preserve div structure and indentation
        const joinedAssignments = assignmentLines.join("");
        bodyText = bodyText.replace(/{AssignmentsList}/g, joinedAssignments);
      }

      // Replace other placeholders using first assignment
      const firstAssignment = visibleAssignments[0];
      return replacePlaceholders(bodyText, firstAssignment);
    },
    [smsTemplates, assignmentModifier, getVisibleAssignments],
  );

  // Update editor content when template changes while editing
  useEffect(() => {
    if (editingLinguistId && editorRef.current) {
      const linguist = linguists.find((l) => l.id === editingLinguistId);
      if (linguist) {
        // Only update if we don't have an edited message (i.e., template just changed)
        if (!linguist.editedMessage) {
          const message = generateMessage(linguist, true);
          editorRef.current.innerHTML = message;
        }
      }
    }
  }, [editingLinguistId, smsTemplates, linguists, generateMessage]);

  const handleTemplateChange = (linguistId, templateId) => {
    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === linguistId
          ? {
              ...ling,
              selectedTemplateId: templateId,
              editedMessage: null,
              isCopied: false,
              isSent: false,
            }
          : ling,
      ),
    );
  };

  // Update editor content when template changes while editing
  useEffect(() => {
    if (!editingLinguistId || !editorRef.current) return;
    const linguist = linguists.find((l) => l.id === editingLinguistId);
    if (!linguist || linguist.editedMessage) return;

    const message = generateMessage(linguist, true);
    if (editorRef.current.innerHTML !== message) {
      editorRef.current.innerHTML = message;
    }
  }, [editingLinguistId, linguists, generateMessage]);

  const handleEdit = (linguistId) => {
    setEditingLinguistId(linguistId);
    const linguist = linguists.find((l) => l.id === linguistId);

    setTimeout(() => {
      if (editorRef.current && linguist) {
        const message =
          linguist.editedMessage || generateMessage(linguist, true);
        editorRef.current.innerHTML = message;
      }
    }, 0);
  };

  const handleDoneEdit = () => {
    const linguist = linguists.find((l) => l.id === editingLinguistId);
    if (!linguist) return;

    // Check if there are any unhandled manual replacements
    const currentMessage =
      linguist.editedMessage || generateMessage(linguist, true);
    if (currentMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (currentMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't save until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    // Save the changes and exit edit mode
    setEditingLinguistId(null);
  };

  const handleCancelEdit = () => {
    // Discard changes and exit edit mode
    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === editingLinguistId
          ? { ...ling, editedMessage: null, isCopied: false, isSent: false }
          : ling,
      ),
    );
    setEditingLinguistId(null);
  };

  const handleCopy = async (linguistId) => {
    const linguist = linguists.find((l) => l.id === linguistId);
    if (!linguist) return;

    // Generate message to copy
    const htmlMessage =
      linguist.editedMessage || generateMessage(linguist, true);

    // Check if there are any unhandled manual replacements
    if (htmlMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (htmlMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't copy until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    // Generate plain text version (strip HTML) for copying
    const plainTextMessage = stripHtmlTags(htmlMessage);

    try {
      await navigator.clipboard.writeText(plainTextMessage);

      setLinguists((prev) =>
        prev.map((ling) =>
          ling.id === linguistId ? { ...ling, isCopied: true } : ling,
        ),
      );

      showSnackbar("Message copied to clipboard", theme, "success");
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      showSnackbar("Failed to copy message", theme, "error");
    }
  };

  const handleSend = (linguistId) => {
    const linguist = linguists.find((l) => l.id === linguistId);
    if (!linguist) return;

    const htmlMessage =
      linguist.editedMessage || generateMessage(linguist, true);

    if (htmlMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (htmlMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't send until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    const plainTextMessage = stripHtmlTags(htmlMessage);
    const smsLink = `sms:${linguist.phoneNumber};?&body=${encodeURIComponent(plainTextMessage)}`;
    window.open(smsLink, "_blank", "noopener");

    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === linguistId ? { ...ling, isSent: true } : ling,
      ),
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0, 0, 0, 0.8)",
          zIndex: 1300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: theme.palette.background.default,
            width: { xs: "100%", sm: "90%", md: "80%", lg: "70%" },
            height: { xs: "95%", sm: "75%" },
            minWidth: { xs: "100%", sm: "600px" },
            maxWidth: "1400px",
            maxHeight: "95%",
            display: "flex",
            borderRadius: "1.5rem",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
            color: theme.palette.text.primary,
            p: "0rem .2rem 1.5rem 0",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              p: "0.75rem 2rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                color="primary"
                size="large"
                onClick={editingLinguistId ? handleCancelEdit : closeOverlay}
              >
                <ChevronLeftRoundedIcon fontSize="inherit" />
              </IconButton>
              <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
                Interpretation Reminders
              </Typography>
            </Box>
            <IconButton
              aria-label="Reminder settings"
              onClick={handleSettingsMenuOpen}
            >
              <MoreVertRoundedIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={isSettingsMenuOpen}
              onClose={handleSettingsMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem disableRipple sx={{ cursor: "default" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={excludeRequestedGlobal}
                      onChange={(e) =>
                        handleExcludeRequestedToggle(e.target.checked)
                      }
                    />
                  }
                  label="Exclude Requested"
                  sx={{ m: 0 }}
                />
              </MenuItem>
              <MenuItem disableRipple sx={{ cursor: "default", py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Requested assignments will be {excludeRequestedGlobal ? "excluded" : "included"}
                </Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: "1rem 2.3rem 1rem 2.5rem",
              height: "100%",
              width: "100%",
              overflowY: "auto",
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.primary.border2}`,
              borderRadius: "1.5rem",
            }}
          >
            <Grid container spacing={2}>
              {/* Date Picker */}
              <Grid size={{ xs: 12 }}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  open={openDatePicker}
                  onOpen={() => setOpenDatePicker(true)}
                  onClose={() => setOpenDatePicker(false)}
                  //readOnly
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      onClick: () => setOpenDatePicker(true),
                      InputProps: {
                        readOnly: true,
                        onClick: () => setOpenDatePicker(true),
                      },
                      inputProps: {
                        readOnly: true,
                        style: { cursor: "pointer" },
                      },
                      sx: {
                        cursor: "pointer",
                        "& input": {
                          cursor: "pointer",
                          userSelect: "none",
                        },
                        "& .MuiInputBase-root": {
                          cursor: "pointer",
                        },
                      },
                    },
                  }}
                />
              </Grid>

              {/* Linguists List */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    //maxHeight: "600px",
                    overflowY: "auto",
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : displayedLinguists.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary">
                        No assignments found for{" "}
                        {selectedDate.format("MMMM DD, YYYY")}
                      </Typography>
                    </Box>
                  ) : editingLinguistId ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {displayedLinguists
                        .filter((l) => l.id === editingLinguistId)
                        .map((linguist) => (
                          <Box
                            key={linguist.id}
                            sx={
                              {
                                //p: 2,
                                //borderRadius: "12px",
                                //border: `2px solid ${theme.palette.primary.main}`,
                                //bgcolor: theme.palette.background.default,
                              }
                            }
                          >
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              gutterBottom
                            >
                              Edit Reminder for {linguist.name}
                            </Typography>

                            <Alert severity="warning" sx={{ mb: 2 }}>
                              This message will not be saved. Copy and send it
                              to the linguist right after making edits.
                            </Alert>

                            {/* Template Select during editing */}
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="SMS Template"
                                variant="outlined"
                                value={linguist.selectedTemplateId || ""}
                                onChange={(e) =>
                                  handleTemplateChange(
                                    linguist.id,
                                    e.target.value,
                                  )
                                }
                                disabled={smsTemplates.length === 0}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#ccc",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#999",
                                    },
                                  },
                                }}
                              >
                                {smsTemplates
                                  .filter(
                                    (t) => t.Type === "Template" || !t.Type,
                                  )
                                  .map((template) => (
                                    <MenuItem
                                      key={template.id}
                                      value={template.id}
                                    >
                                      {template.Name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid size={{ xs: 12, md: 8 }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Message
                                </Typography>
                                <Box
                                  ref={editorRef}
                                  contentEditable
                                  suppressContentEditableWarning
                                  onInput={(e) => {
                                    const htmlContent =
                                      e.currentTarget?.innerHTML;
                                    if (htmlContent !== undefined) {
                                      setLinguists((prev) =>
                                        prev.map((ling) =>
                                          ling.id === editingLinguistId
                                            ? {
                                                ...ling,
                                                editedMessage: htmlContent,
                                                isCopied: false,
                                                isSent: false,
                                              }
                                            : ling,
                                        ),
                                      );
                                    }
                                  }}
                                  sx={{
                                    height: 340,
                                    maxHeight: 340,
                                    p: 1.5,
                                    borderRadius: "12px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    bgcolor: theme.palette.background.default,
                                    outline: "none",
                                    "&:focus": {
                                      borderColor: theme.palette.primary.main,
                                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                                    },
                                    overflowY: "auto",
                                    whiteSpace: "pre-wrap",
                                    "& ul, & ol": {
                                      paddingLeft: "1.5rem",
                                    },
                                    "& li": {
                                      marginBottom: "0.25rem",
                                    },
                                  }}
                                />
                              </Grid>

                              <Grid size={{ xs: 12, md: 4 }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Modifiers
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    p: 1.5,
                                    borderRadius: "12px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    bgcolor: theme.palette.background.default,
                                    height: 340,
                                    maxHeight: 340,
                                    overflowY: "auto",
                                  }}
                                >
                                  {smsModifiers.length > 0 ? (
                                    <>
                                      {/* Group modifiers by channel */}
                                      {["SMS", "Email"].map((channel) => {
                                        const channelModifiers =
                                          smsModifiers.filter(
                                            (m) =>
                                              m.Type === "Modifier" &&
                                              m.Channel === channel,
                                          );

                                        if (channelModifiers.length === 0)
                                          return null;

                                        return (
                                          <Box key={channel}>
                                            <Typography
                                              variant="caption"
                                              fontWeight={700}
                                              sx={{
                                                color:
                                                  theme.palette.text.secondary,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                mb: 0.5,
                                                display: "block",
                                              }}
                                            >
                                              {channel}
                                            </Typography>
                                            {channelModifiers.map(
                                              (modifier) => (
                                                <Box
                                                  key={modifier.id}
                                                  onMouseDown={
                                                    handleModifierMouseDown
                                                  }
                                                  onClick={() =>
                                                    insertModifier(modifier)
                                                  }
                                                  sx={{
                                                    border: `1px solid ${theme.palette.primary.border2}`,
                                                    borderRadius: "10px",
                                                    p: 1,
                                                    mb: 1,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                      borderColor:
                                                        theme.palette.primary
                                                          .main,
                                                      bgcolor: `${theme.palette.primary.main}0a`,
                                                    },
                                                  }}
                                                >
                                                  <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                  >
                                                    {modifier.Name}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No modifiers available
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{ borderRadius: "8px" }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleDoneEdit}
                                sx={{ borderRadius: "8px" }}
                              >
                                Done
                              </Button>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {displayedLinguists.map((linguist) => {
                        const visibleAssignments = getVisibleAssignments(linguist);
                        const requestedCount = (linguist.requestedAssignments || []).length;

                        return (
                        <Box
                          key={linguist.id}
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            {/* Linguist Info */}
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {linguist.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {visibleAssignments.length} assignment
                                {visibleAssignments.length !== 1 ? "s" : ""}
                                {requestedCount > 0
                                  ? ` (${requestedCount} Requested)`
                                  : ""}
                              </Typography>
                            </Grid>

                            {/* Template Select */}
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="SMS Template"
                                variant="outlined"
                                value={linguist.selectedTemplateId || ""}
                                onChange={(e) =>
                                  handleTemplateChange(
                                    linguist.id,
                                    e.target.value,
                                  )
                                }
                                disabled={smsTemplates.length === 0}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#ccc",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#999",
                                    },
                                  },
                                }}
                              >
                                {smsTemplates
                                  .filter(
                                    (t) => t.Type === "Template" || !t.Type,
                                  )
                                  .map((template) => (
                                    <MenuItem
                                      key={template.id}
                                      value={template.id}
                                    >
                                      {template.Name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>

                            {/* Action Buttons */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditRoundedIcon />}
                                  onClick={() => handleEdit(linguist.id)}
                                  disabled={!linguist.selectedTemplateId}
                                  sx={{ borderRadius: "8px" }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ContentCopyRoundedIcon />}
                                  onClick={() => handleCopy(linguist.id)}
                                  disabled={!linguist.selectedTemplateId}
                                  sx={{ borderRadius: "8px" }}
                                >
                                  Copy
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleSend(linguist.id)}
                                  disabled={!linguist.selectedTemplateId}
                                  sx={{ borderRadius: "8px" }}
                                >
                                  Send
                                </Button>
                              </Box>
                            </Grid>

                            {/* Status */}
                            <Grid size={{ xs: 12, md: 2 }}>
                              {linguist.isSent ? (
                                <Chip
                                  icon={<CheckCircleRoundedIcon />}
                                  label="Sent"
                                  color="success"
                                  size="small"
                                />
                              ) : linguist.isCopied ? (
                                <Chip
                                  icon={<CheckCircleRoundedIcon />}
                                  label="Copied"
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    bgcolor: "white",
                                    color: theme.palette.text.secondary,
                                  }}
                                />
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default InterpretationReminders;
